import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import type { SlotTransformSpec, Transform } from '../../../types/adaptations'
import { ImpactFunctionVisualisation } from './TransformEditor'

interface Props {
  open: boolean
  onClose: () => void
  slotLabel: string
  transforms: Transform[]
  spec: SlotTransformSpec
}

const ImpactFunctionBrowserDialog = ({ open, onClose, slotLabel, transforms, spec }: Props) => {
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)

  useEffect(() => {
    if (!open) return
    setQuery('')
    const firstOrganisationFunction = transforms.find(transform => transform.organisation_id != null)
    setSelectedId((firstOrganisationFunction ?? transforms[0])?.id ?? null)
  }, [open, transforms])

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    if (!normalized) return transforms
    return transforms.filter(transform =>
      [transform.label, transform.description].some(value => value?.toLowerCase().includes(normalized)),
    )
  }, [query, transforms])

  const organisationFunctions = filtered.filter(transform => transform.organisation_id != null)
  const platformFunctions = filtered.filter(transform => transform.organisation_id == null)
  const selected = transforms.find(transform => transform.id === selectedId) ?? null

  const updateQuery = (nextQuery: string) => {
    setQuery(nextQuery)
    const normalized = nextQuery.trim().toLowerCase()
    const matches = normalized
      ? transforms.filter(transform =>
          [transform.label, transform.description].some(value => value?.toLowerCase().includes(normalized)),
        )
      : transforms
    setSelectedId(current => (matches.some(transform => transform.id === current) ? current : matches[0]?.id ?? null))
  }

  const renderGroup = (label: string, functions: Transform[]) => {
    if (functions.length === 0) return null
    return (
      <li key={label}>
        <ul style={{ padding: 0 }}>
          <ListSubheader disableSticky>{label}</ListSubheader>
          {functions.map(transform => (
            <ListItemButton
              key={transform.id}
              selected={transform.id === selectedId}
              onClick={() => setSelectedId(transform.id)}
              sx={{ borderRadius: 1, mx: 0.5, mb: 0.25 }}
            >
              <ListItemText
                primary={transform.label}
                primaryTypographyProps={{ variant: 'body2', fontWeight: transform.id === selectedId ? 700 : 500 }}
              />
            </ListItemButton>
          ))}
        </ul>
      </li>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Impact functions</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Browse the functions available for {slotLabel}.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '280px minmax(0, 1fr)' },
            gap: 2.5,
            minWidth: 0,
          }}
        >
          <Stack spacing={1} sx={{ minWidth: 0 }}>
            <TextField
              size="small"
              value={query}
              onChange={event => updateQuery(event.target.value)}
              placeholder="Search impact functions"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <List
              dense
              subheader={<li />}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 0.5,
                maxHeight: { xs: 220, md: 520 },
                overflowY: 'auto',
              }}
            >
              {renderGroup('Your organisation', organisationFunctions)}
              {renderGroup('Platform defaults', platformFunctions)}
              {filtered.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No impact functions match your search.
                </Typography>
              )}
            </List>
          </Stack>

          <Box sx={{ minWidth: 0 }}>
            {selected ? (
              <>
                <Typography variant="h6" sx={{ mb: 1.5 }}>
                  {selected.label}
                </Typography>
                <ImpactFunctionVisualisation transform={selected} spec={spec} />
              </>
            ) : (
              <Box
                sx={{
                  minHeight: 260,
                  display: 'grid',
                  placeItems: 'center',
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Typography color="text.secondary">No impact functions are available for this slot.</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default ImpactFunctionBrowserDialog
